import { Injectable } from '@angular/core';
import {
  CancelOrUnlockReviewRequest,
  CollaborationHistoryEnum,
  CostReviewAttributes,
  CostReviewBaseAttributes,
  CostReviewDatabaseRecord,
  CostReviewerDetails,
  CostReviewFireEnUsAttributes,
  CostReviewSecurityEnUsAttributes,
  CostReviewStatus,
  CostReviewType,
  ForcePublishDetails,
  NewCostReviewAttributes_SOL,
  NewCostReviewAttributesBase,
  CostReviewEmailAttachment,
  CostReviewElectricalAttributes,
  MultiPdfLocalLibraryReports,
  SubmittalPackage,
  EstimateData
} from '../../models/dtos';

import { CONSTANTS } from '../../Constants/Constants';
import { CommonDataService } from '../common-data.service';
import { UserProfileService } from '../common/user-profile.service';
import { ICostReviewRequestCCRecipient } from '../../models/common/ICostReviewRequestCCRecipient';
import { TranslateService } from '@ngx-translate/core';
import { FormGroup } from '@angular/forms';
import { EmailService } from '../email.service';
import { BehaviorSubject } from 'rxjs';
import { PdfBufferService } from '../reports/pdf-buffer/pdf-buffer.service';
import { ReportList } from '../../models/dtos/Enums/ReportList';
import { reportItems } from '../../models/reports/ReportNameList';
import { CryptoUtilityService } from '../utils/crypto-utility.service';
import { ReportsCommonService } from '../reports/reports-common/reports-common.service';
import { ISubmittalPackages } from '../../models/properties/ISubmittalPackages';

export interface CostReviewApprovedDetails {
  approvedSellPrice: number;
  approvedMarginPercent: number;
  approvalChangePercent: number;
  isApprovedMarginAvailable: boolean;
  currentStatus: number;
}

export interface AutoUnlockResult {
  isReReviewNeeded: boolean;
  autoUnlockByMargin: boolean;
  autoUnlockBySellPrice: boolean;
  autoUnlockMessage: string;
}

export interface AutoUnlockState {
  isAutoUnlocked: boolean;
  message: string;
  statusTranslationKey: string;
}

@Injectable({
  providedIn: 'root'
})
export class CostReviewRecordBuilderService {

  private _costReviewTableData: CostReviewDatabaseRecord;
  private _costReviewTableDataCopy: CostReviewDatabaseRecord;
  private _salesPersonName = CONSTANTS.EMPTY_STRING;
  private _reviewerList: CostReviewerDetails[];
  private _dataSourceCCRecipient: ICostReviewRequestCCRecipient[];
  private _originalStatusBeforeAutoUnlock: number | null = null;
  private _isAutoUnlocked: boolean = false;
  private _autoUnlockMessage: string = '';
  private readonly _autoUnlockState$ = new BehaviorSubject<AutoUnlockState>({ isAutoUnlocked: false, message: '', statusTranslationKey: '' });
  readonly autoUnlockState$ = this._autoUnlockState$.asObservable();

  get costReviewTableData(): CostReviewDatabaseRecord {
    const liveRecord = this.commonDataService.getCostReviewData();
    if (liveRecord) {
      this._costReviewTableData = liveRecord;
    }
    return this._costReviewTableData;
  }

  get salesPersonName(): string {
    return this._salesPersonName;
  }

  set salesPersonName(value: string | null | undefined) {
    this._salesPersonName = value ?? CONSTANTS.EMPTY_STRING;
    this.commonDataService.setCostReviewSalesPersonName(this._salesPersonName);
  }

  set costReviewTableData(value: CostReviewDatabaseRecord) {
    this._costReviewTableData = value;
    this.salesPersonName = this.getCostReviewRequestorName();
  }

  get costReviewTableDataCopy(): CostReviewDatabaseRecord {
    return this._costReviewTableDataCopy;
  }

  set costReviewTableDataCopy(value: CostReviewDatabaseRecord) {
    this._costReviewTableDataCopy = value;
  }

  get isAutoUnlocked(): boolean {
    return this._isAutoUnlocked;
  }

  set isAutoUnlocked(value: boolean) {
    this._isAutoUnlocked = value;
  }

  set originalStatusBeforeAutoUnlock(value: number | null) {
    this._originalStatusBeforeAutoUnlock = value;
  }

  set autoUnlockMessage(value: string) {
    this._autoUnlockMessage = value;
  }

  get reviewerList(): CostReviewerDetails[] {
    return this._reviewerList;
  }
  
  set reviewerList(value: CostReviewerDetails[]) {
    this._reviewerList = value;
  }

  get dataSourceCCRecipient(): ICostReviewRequestCCRecipient[] {
    return this._dataSourceCCRecipient;
  }

  set dataSourceCCRecipient(value: ICostReviewRequestCCRecipient[]) {
    this._dataSourceCCRecipient = value;
  }

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly userProfileService: UserProfileService,
    private readonly translateService: TranslateService,
    private readonly emailService: EmailService,
    private readonly cryptoUtilityService: CryptoUtilityService,
    private readonly pdfBufferService: PdfBufferService,
    private readonly reportsCommonService: ReportsCommonService,
  ) { }

  getCostReviewRequestorName(): string {
    const preferenceSalesPersonName = this.commonDataService.getPreferenceData()?.preferences?.salesPersonName;
    const loggedInUserName = this.userProfileService.userInfo?.name || CONSTANTS.EMPTY_STRING;

    if (this.commonDataService.isCollaborationEstimate()) {
      return preferenceSalesPersonName || loggedInUserName;
    }

    return this.salesPersonName || loggedInUserName;
  }

  getBusinessSpecificReviewObject(): CostReviewAttributes | CostReviewFireEnUsAttributes | CostReviewSecurityEnUsAttributes | CostReviewElectricalAttributes {
    const business = this.commonDataService.getBusiness(); 
    switch (business) {
      case CONSTANTS.BUSINESS_AUTOMATION:
      case CONSTANTS.BUSINESS_MECHANICAL:
        return this.generateAutomationReviewObject();
      case CONSTANTS.BUSINESS_FIRE:
        return this.generateFireReviewObject();
      case CONSTANTS.BUSINESS_SECURITY:
        return this.generateSecurityReviewObject();
      case CONSTANTS.BUSINESS_ELECTRICAL:
        return this.generateElectricalReviewObject();
      default:
        return null;
    }
  }

  private generateAutomationReviewObject(): CostReviewAttributes {
    const source = this.costReviewTableData?.reviewObject as CostReviewAttributes;
    if (!source) {
      return null;
    }

    return this.generateCostReviewBaseAttributeRequestData(source, 'CostReviewAttributes');
  }

  private generateFireReviewObject(): CostReviewFireEnUsAttributes {
    const source = this.costReviewTableData?.reviewObject as CostReviewFireEnUsAttributes;
    if (!source) {
      return null;
    }

    return this.generateCostReviewBaseAttributeRequestData(source, 'CostReviewFireEnUsAttributes');
  }

  private generateSecurityReviewObject(): CostReviewSecurityEnUsAttributes {
    const source = this.costReviewTableData?.reviewObject as CostReviewSecurityEnUsAttributes;
    if (!source) {
      return null;
    }

    return {
      ...this.generateCostReviewBaseAttributeRequestData(source, 'CostReviewSecurityEnUsAttributes'),
      reasonForLaborVariance: source.reasonForLaborVariance || CONSTANTS.EMPTY_STRING
    };
  }

  private generateElectricalReviewObject(): CostReviewAttributes | CostReviewElectricalAttributes {
    const source = this.costReviewTableData?.reviewObject;
    if (!source || (source as CostReviewElectricalAttributes)?.costReviewScopeOfWork == null) {
      return this.generateAutomationReviewObject();
    }

    return this.generateCostReviewBaseAttributeRequestData(source as CostReviewElectricalAttributes, 'CostReviewElectricalAttributes');
  }

  private generateCostReviewBaseAttributeRequestData<T extends CostReviewBaseAttributes>(source: T, costReviewType: string): T {
    return {
      ...source,
      costReviewNodeType: costReviewType,
    } as T;
  }

  generateNewCostReviewAttributeRequestData(): NewCostReviewAttributesBase {
    const costReviewObject = this.commonDataService.getCostReviewData();
    if (!costReviewObject?.newReviewObject || !costReviewObject?.reviewObject) {
      return null;
    }
    const newReviewObject = costReviewObject.newReviewObject as NewCostReviewAttributes_SOL;
    const reviewObject = costReviewObject.reviewObject;
    const newCostReviewAttributesBase: NewCostReviewAttributes_SOL = {
      newCostReviewType: 'NewCostReviewAttributes_SOL',
      costReviewType: newReviewObject.costReviewType,
      estimateName: reviewObject.costReviewSummary.projectName,
      accountName: reviewObject.costReviewSummary.customerName,
      shipToAddress: reviewObject.costReviewSummary.shipToLocation,
      sellPrice: newReviewObject.sellPrice,
      tdc: newReviewObject.tdc,
      gm: newReviewObject.gm,
      proposalLink: newReviewObject.proposalLink || CONSTANTS.EMPTY_STRING,
      opportunityLink: newReviewObject.opportunityLink || CONSTANTS.EMPTY_STRING,
      laborHoursDistributionDetails: newReviewObject.laborHoursDistributionDetails,
      costByPartNumberDetails: newReviewObject.costByPartNumberDetails,
      subsAndMiscCostsDetails: newReviewObject.subsAndMiscCostsDetails,
      laborByLevelDetails: newReviewObject.laborByLevelDetails,
      taskByLevelWithNotesDetails: newReviewObject.taskByLevelWithNotesDetails,
      hoursByLevelDetails: newReviewObject.hoursByLevelDetails,
      costElementsZTC: newReviewObject.costElementsZTC,
      autoRiskDollars: newReviewObject.autoRiskDollars,
      autoRiskPercentageOfTDC: newReviewObject.autoRiskPercentageOfTDC,
      conditionByLevelWithNotesDetails: newReviewObject.conditionByLevelWithNotesDetails
    };
    return newCostReviewAttributesBase;
  }

  convertMarginPercent(marginPercent: number, isMarkUp: boolean): number {
    if (isMarkUp) {
      const capped = marginPercent === CONSTANTS.HUNDRED ? CONSTANTS.NINETY_NINE_POINT_NINE_NINE : marginPercent;
      return (capped / (CONSTANTS.HUNDRED + capped)) * CONSTANTS.HUNDRED;
    }
    return marginPercent;
  }

  private getCCRecipientEmails(): string[] {
    const emails = [
      this.userProfileService.userInfo?.mail ?? '',
      ...(this.dataSourceCCRecipient ?? []).map(recipient => recipient.email)
    ].map(email => email.trim()).filter(email => email !== '');

    return emails.filter((email, index) =>
      emails.findIndex(recipient => recipient.toLowerCase() === email.toLowerCase()) === index
    );
  }

  generateCCUserList(): string {
    return this.getCCRecipientEmails().join(', ');
  }

  /** Cancelling closes the review, but the manually added reviewers stay available for the next request without their old due dates. 
   * Policy mandated approvers are excluded because they are re-fetched on every new request. 
   */
      retainReviewersForNextRequest(reviewers: CostReviewerDetails[] | null): CostReviewerDetails[] {
    return (reviewers ?? [])
      .filter(reviewer => !reviewer.isPolicyMandatedApprover)
      .map((reviewer): CostReviewerDetails => ({ ...reviewer, requiredBy: null }));
  }

    buildCancelOrUnlockRequest(costReviewData: CostReviewDatabaseRecord, forcePublishDetails: ForcePublishDetails | null, estimateData: EstimateData): CancelOrUnlockReviewRequest {
    costReviewData.reviewObject = this.getBusinessSpecificReviewObject();
    costReviewData.newReviewObject = this.generateNewCostReviewAttributeRequestData();
    return {
      estimateId: costReviewData.estimateId || this.commonDataService.getEstimateId(),
      isCollaborativeEstimate: this.commonDataService.isCollaborationEstimate(),
      userId: this.userProfileService.userInfo?.gid || CONSTANTS.EMPTY_STRING,
      userEmailId: this.userProfileService.userInfo?.mail || CONSTANTS.EMPTY_STRING,
      cultureId: this.commonDataService.getEstimateCultureId() || CONSTANTS.ONE,
      business: estimateData.business,
      costReviewData,
      forcePublishDetails
    };
  }

  buildUnlockForcePublishDetails(isAutoUnlock: boolean): ForcePublishDetails | null {
    if (!this.commonDataService.isCollaborationEstimate()) {
      return null;
    }
    const operation = isAutoUnlock
      ? CollaborationHistoryEnum.CostReviewAutoUnlocked
      : CollaborationHistoryEnum.CostReviewUnlocked;

    return {
      forcePublishAdditionalInfo: CONSTANTS.ESTIMATE_FORCE_PUBLISH_COST_REVIEW_UNLOCK,
      operation,
      modificationNotes: this.translateService.instant('MODIFICATION_NOTES_COST_REVIEW_UNLOCKED'),
      estimateCheckoutAdditionalInfo: CONSTANTS.ESTIMATE_ADD_ON_UNLOCK_COST_REVIEW,
      isEstimateBeingCheckedOutAfterPublish: true
    };
  }

  public mapStatusToTranslationKey(status: number | null): string {
    switch (status) {
      case CostReviewStatus.InProcess:
        return 'COST_REVIEW_STATUS_IN_PROGRESS';
      case CostReviewStatus.Pending:
        return 'COST_REVIEW_STATUS_PENDING';
      case CostReviewStatus.Approved:
        return 'COST_REVIEW_STATUS_APPROVED';
      case CostReviewStatus.Denied:
        return 'COST_REVIEW_STATUS_DENIED';
      case CostReviewStatus.NotStarted:
      case CostReviewStatus.None:
      default:
        return 'COST_REVIEW_STATUS_NOT_STARTED';
    }
  }
  
  async openMailClient(emailSubject: string, emailBody: string, recipientEmails: string[]): Promise<void> {
    const to = recipientEmails.join(';');
    const body = emailBody
      .replaceAll('<NewLine>', '\n')
      .replace(/<a\s+href="([^"]+)"[^>]*>.*?<\/a>/gi, '$1')
      .replaceAll('{{', '{')
      .replaceAll('}}', '}');
    const ccEmails = this.getCCRecipientEmails();

    this.emailService.openMailToWithLengthCheck(to, emailSubject, body, ccEmails);
  }

  getSelectedReports(form: FormGroup): string[] {
    return [form.controls['report1'].value, form.controls['report2'].value, form.controls['report3'].value].filter(v => v && v !== '-1');
  }

  async getSelectedReportsPdf(reports: string[]): Promise<CostReviewEmailAttachment[]> {
    const attachments: CostReviewEmailAttachment[] = [];
    //(Race Condition) This is sequential to avoid overwhelming the PDF generation service with multiple requests at once.
    for (const selection of reports) {
      if (!selection || selection === CONSTANTS.NEGATIVE_ONE_STRING) {
        continue;
      }

      const reportId = Number(selection);
      let pdfBytes: Uint8Array;
      let reportName: string;

      if (reportId > 0) {
        pdfBytes = await this.pdfBufferService.getReportPdfBytes(reportId as ReportList);
        reportName = this.translateService.instant(reportItems.find(item => item.id === reportId)?.reportName || `Report-${reportId}`);
      } else {
        const packageItem = this.reportsCommonService.getMultiPdfDropdownItems()
          .find(item => item.id?.toLowerCase() === selection.toLowerCase());
        const reportIds = this.reportsCommonService.getReportSequence(selection)
          .filter(id => reportItems.some(item => item.id === id)) as ReportList[];

        if (!packageItem || reportIds.length === 0) {
          throw new Error(`Submittal package "${selection}" was not found or contains no valid reports.`);
        }

        pdfBytes = await this.pdfBufferService.getMultiReportPdfBytes(reportIds);
        reportName = packageItem.name;
      }

      attachments.push({
        fileName: `${reportName}.pdf`,
        contentType: 'application/pdf',
        contentBase64: await this.cryptoUtilityService.toBase64(pdfBytes)
      });
    }

    return attachments;
  }

  autoUnlockCostReview(currentSellPrice: number, currentMargin: number, approvalChangePercent: number): AutoUnlockResult {
    const noUnlockResult: AutoUnlockResult = { isReReviewNeeded: false, autoUnlockByMargin: false, autoUnlockBySellPrice: false, autoUnlockMessage: '' };

    let record = this.costReviewTableData;

    if (!record) {
      return noUnlockResult;
    }

    const statusToCheck = this._isAutoUnlocked
      ? this._originalStatusBeforeAutoUnlock
      : record.status;

    const approvedDetails: CostReviewApprovedDetails = {
      approvedSellPrice: record.approvedSellPrice || 0,
      approvedMarginPercent: record.approvedMarginPercent || 0,
      approvalChangePercent,
      isApprovedMarginAvailable: (record.approvedMarginPercent ?? null) !== null && record.approvedMarginPercent !== 0,
      currentStatus: statusToCheck || CostReviewStatus.None
    };

    const result = this.isReReviewNeeded(currentSellPrice, currentMargin, approvedDetails);

    if (result.isReReviewNeeded) {
      if (!this._isAutoUnlocked) {
        this._originalStatusBeforeAutoUnlock = record.status;
        this._costReviewTableDataCopy = JSON.parse(JSON.stringify(record));
        // Raised only on the transition into auto-unlock; the review tab clears it on visit.
        this.commonDataService.setIsReviewCancelledBySystem = true;
      }
      record.status = CostReviewStatus.NotStarted;
      record.costReviewers = [];
      record.autoUnlockByMargin = result.autoUnlockByMargin;
      record.autoUnlockBySellPrice = result.autoUnlockBySellPrice;
      if (record.reviewObject) {
        record.reviewObject.isAutoUnlockBySystem = true;
      }
      this._isAutoUnlocked = true;
      this._autoUnlockMessage = 'COST_REVIEW_ROUTING_CANCELLED_BY_SYSTEM';
      result.autoUnlockMessage = this._autoUnlockMessage;
      this._autoUnlockState$.next({
        isAutoUnlocked: true,
        message: this._autoUnlockMessage,
        statusTranslationKey: 'COST_REVIEW_STATUS_NOT_STARTED'
      });
    } else if (this._isAutoUnlocked) {
      const originalStatus = this._originalStatusBeforeAutoUnlock;
      const restoredRecord: CostReviewDatabaseRecord = this._costReviewTableDataCopy
        ? JSON.parse(JSON.stringify(this._costReviewTableDataCopy))
        : record;
      restoredRecord.status = originalStatus ?? restoredRecord.status;
      restoredRecord.autoUnlockByMargin = false;
      restoredRecord.autoUnlockBySellPrice = false;
      if (restoredRecord.reviewObject) {
        restoredRecord.reviewObject.isAutoUnlockBySystem = false;
      }
      this.costReviewTableData = restoredRecord;
      this.commonDataService.setCostReviewData(restoredRecord);
      this._isAutoUnlocked = false;
      this._autoUnlockMessage = '';
      this._autoUnlockState$.next({
        isAutoUnlocked: false,
        message: '',
        statusTranslationKey: this.mapStatusToTranslationKey(originalStatus)
      });
      this._originalStatusBeforeAutoUnlock = null;
      this._costReviewTableDataCopy = null;
      this.commonDataService.setIsReviewCancelledBySystem = false;
    }

    return result;
  }

  restoreAutoUnlockMessageFromSavedRecord(): void{
    const record = this.costReviewTableData;
  if (record?.status === CostReviewStatus.NotStarted && record.reviewObject?.isAutoUnlockBySystem) {
    this._autoUnlockMessage = 'COST_REVIEW_ROUTING_CANCELLED_BY_SYSTEM';
    // _isAutoUnlocked stays false so autoUnlockCostReview() never tries to revert the status
    this._autoUnlockState$.next({ isAutoUnlocked: false, message: this._autoUnlockMessage, statusTranslationKey: '' });
  }
  }

  /**
   * Clears the revert-to-previous-status tracking once the auto-unlocked status has been
   * persisted. Call this only after a successful save, otherwise a failed save would leave the
   * record unlocked in the UI with no way back to its original status.
   */
  commitAutoUnlock(): void {
    if (this._isAutoUnlocked) {
      this._originalStatusBeforeAutoUnlock = null;
      this._costReviewTableDataCopy = null;
      this._isAutoUnlocked = false;
    }
  }

  resetAutoUnlockState(status: number): void {
    this._originalStatusBeforeAutoUnlock = null;
    this._costReviewTableDataCopy = null;
    this._isAutoUnlocked = false;
    this._autoUnlockMessage = '';
    this.commonDataService.setIsReviewCancelledBySystem = false;
    this._autoUnlockState$.next({
      isAutoUnlocked: false,
      message: '',
      statusTranslationKey: this.mapStatusToTranslationKey(status)
    });
    const record = this.costReviewTableData;
    if (record) {
      record.autoUnlockByMargin = false;
      record.autoUnlockBySellPrice = false;
    }
    if (record?.reviewObject) {
      record.reviewObject.isAutoUnlockBySystem = false;
    }
  }

  isReReviewNeeded(currentSellPrice: number, currentMargin: number, approvedDetails: CostReviewApprovedDetails): AutoUnlockResult {
    const result: AutoUnlockResult = { isReReviewNeeded: false, autoUnlockByMargin: false, autoUnlockBySellPrice: false, autoUnlockMessage: '' };

    if (approvedDetails.currentStatus !== CostReviewStatus.InProcess && approvedDetails.currentStatus !== CostReviewStatus.Approved) {
      return result;
    }

    const approvalChangePercent = approvedDetails.approvalChangePercent;
    const approvedSellPrice = approvedDetails.approvedSellPrice;

    if (approvedSellPrice !== 0) {
      const sellPriceChange = Math.abs(currentSellPrice - approvedSellPrice);
      const sellPriceThreshold = Math.abs(approvedSellPrice * approvalChangePercent / CONSTANTS.HUNDRED);
      if (sellPriceChange > sellPriceThreshold) {
        result.isReReviewNeeded = true;
        result.autoUnlockBySellPrice = true;
      }
    }

    if (approvedDetails.isApprovedMarginAvailable) {
      const marginDrop = approvedDetails.approvedMarginPercent - currentMargin;
      if (marginDrop > approvalChangePercent) {
        result.isReReviewNeeded = true;
        result.autoUnlockByMargin = true;
      }
    }

    return result;
  }

  /**
   * Resets all cost review data to default values.
   */
  resetCostReviewData(): void {
    try {
      this._costReviewTableData = null;
      this.salesPersonName = CONSTANTS.EMPTY_STRING;
      this._reviewerList = null;
      this._dataSourceCCRecipient = null;
      this._originalStatusBeforeAutoUnlock = null;
      this._costReviewTableDataCopy = null;
      this._isAutoUnlocked = false;
      this._autoUnlockMessage = '';
      this.commonDataService.setIsReviewCancelledBySystem = false;
      this._autoUnlockState$.next({ isAutoUnlocked: false, message: '', statusTranslationKey: '' });
    } catch (error) {
      console.error('Error resetting cost review data:', error);
    }
  }

  getReportDropdownItems(): ISubmittalPackages[] {
  const visibleReportItems = this.reportsCommonService.getVisibleReportItems();
  const reportItems: ISubmittalPackages[] = visibleReportItems
    .filter(item => item.level === 0 && item.reportName !== 'MULTI_PDF')
    .map(item => ({
      type: 'option',
      name: this.translateService.instant(item.reportName),
      id: String(item.id),
      disabled: false,
    }));
  const multiPdfItems = this.reportsCommonService.getMultiPdfDropdownItems();
  return [...reportItems, ...multiPdfItems];
}

}
